import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import GenericHeader from "../../../Componentes/GenericHeader";
import SortableTable from "../../../Componentes/Utils/SortableTable";
import FilterContainer from "../../../Componentes/Utils/FilterContainer";
import Filters from "../../../Componentes/Utils/Filters";
import MultiselectCheck from "../../../Componentes/Utils/MultiselectCheck";
import { useModal } from "../../../contexts/ModalContext";
import useApi from "../../../hooks/useApi";
import ModalPreencherItem from "./ModalPreencherItem";

const PageContent = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
    box-sizing: border-box;
    overflow: hidden;
`;

const TableFlexFill = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;

    & > div {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }
`;

const ResumosRow = styled.div`
    display: flex;
    flex-shrink: 0;
    gap: 10px;
    align-items: stretch;

    @media (max-width: 768px) {
        width: 100%;
    }
`;

const PontosResumo = styled.div`
    width: 120px;
    flex-shrink: 0;
    background-color: #f3f3f3;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 12px;
    padding: 10px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 2px;

    @media (max-width: 768px) {
        flex: 1;
        width: auto;
        min-width: 0;
    }
`;

const PontosValor = styled.span`
    font-size: 28px;
    font-weight: bold;
    color: #000;
    line-height: 1.1;
`;

const PontosLabel = styled.span`
    font-size: 12px;
    font-weight: bold;
    color: #333;
`;

function uniqueSortedStrings(values) {
    return [...new Set(values.map((v) => String(v ?? "").trim()).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, "pt-BR", { sensitivity: "base" })
    );
}

function toValueSet(selected) {
    if (!Array.isArray(selected) || selected.length === 0) return null;
    return new Set(
        selected.map((item) =>
            item != null && typeof item === "object" && "value" in item ? item.value : item
        )
    );
}

function itemEstaPreenchido(row) {
    return String(row.resposta ?? "").trim() !== "";
}

function formatPontos(valor) {
    const n = Number(valor ?? 0);
    if (Number.isNaN(n)) return "0,00";
    return n.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

function tipoFiltroLabel(row) {
    const codigo = String(row.tipo_codigo ?? "").trim();
    const nome = String(row.tipo_nome ?? "").trim();
    if (codigo && nome) return `${codigo} — ${nome}`;
    return nome || codigo || "";
}

function aplicarFiltros(lista, filtros) {
    const descricaoBusca = filtros.descricao.trim().toUpperCase();
    const tiposSet = toValueSet(filtros.selectedTipos);
    const respostasSet = toValueSet(filtros.selectedRespostas);
    const preenchidoSet = toValueSet(filtros.selectedPreenchido);

    return lista.filter((row) => {
        if (tiposSet && !tiposSet.has(tipoFiltroLabel(row))) return false;

        const descricao = String(row.descricao ?? "").toUpperCase();
        if (descricaoBusca && !descricao.includes(descricaoBusca)) return false;

        const resposta = String(row.resposta ?? "").trim();
        if (respostasSet && !respostasSet.has(resposta)) return false;

        if (preenchidoSet) {
            const preenchido = itemEstaPreenchido(row);
            const querPreenchido = preenchidoSet.has("PREENCHIDO");
            const querNaoPreenchido = preenchidoSet.has("NÃO PREENCHIDO");

            if (querPreenchido && querNaoPreenchido) {
                return true;
            }
            if (querPreenchido && !preenchido) return false;
            if (querNaoPreenchido && preenchido) return false;
        }

        return true;
    });
}

const Content = ({ onProssmapLoaded }) => {
    const { id } = useParams();
    const api = useApi();
    const { openModal, isModalOpen } = useModal();

    const [loading, setLoading] = useState(true);
    const [itens, setItens] = useState([]);
    const [prossmap, setProssmap] = useState(null);
    const [itemSelecionado, setItemSelecionado] = useState(null);

    const [selectedTipos, setSelectedTipos] = useState([]);
    const [descricao, setDescricao] = useState("");
    const [selectedRespostas, setSelectedRespostas] = useState([]);
    const [selectedPreenchido, setSelectedPreenchido] = useState([]);

    const fetchProssmap = useCallback(async () => {
        if (!id) return;

        setLoading(true);
        try {
            const { data } = await api.get(`/Painel/API/Prossmap/Get/index.php?id=${id}`);
            const payload = data?.data ?? data;
            const prossmapData = payload?.prossmap ?? null;
            setProssmap(prossmapData);
            setItens(Array.isArray(payload?.itens) ? payload.itens : []);
            if (onProssmapLoaded) onProssmapLoaded(prossmapData);
        } catch (error) {
            console.error("Erro ao buscar itens do PROSSMAP:", error);
            setProssmap(null);
            setItens([]);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        fetchProssmap();
    }, [fetchProssmap]);

    const filtros = useMemo(
        () => ({
            selectedTipos,
            descricao,
            selectedRespostas,
            selectedPreenchido,
        }),
        [selectedTipos, descricao, selectedRespostas, selectedPreenchido]
    );

    const itensFiltrados = useMemo(
        () => aplicarFiltros(itens, filtros),
        [itens, filtros]
    );

    const pontuacaoProssmap = Number(prossmap?.pontuacao ?? 0);

    const progressoItens = useMemo(() => {
        const total = itens.length;
        const preenchidos = itens.filter(itemEstaPreenchido).length;
        return { preenchidos, total };
    }, [itens]);

    const opcoesTipo = useMemo(
        () => uniqueSortedStrings(itens.map((r) => tipoFiltroLabel(r))),
        [itens]
    );
    const opcoesTipoMs = useMemo(
        () => opcoesTipo.map((opt) => ({ value: opt, label: opt })),
        [opcoesTipo]
    );
    const opcoesRespostaMs = useMemo(
        () => [
            { value: "C", label: "C" },
            { value: "OM", label: "OM" },
            { value: "N/A", label: "N/A" },
            { value: "NC", label: "NC" },
        ],
        []
    );
    const opcoesPreenchidoMs = useMemo(
        () => [
            { value: "PREENCHIDO", label: "PREENCHIDO" },
            { value: "NÃO PREENCHIDO", label: "NÃO PREENCHIDO" },
        ],
        []
    );

    const handleAbrirItem = (row) => {
        setItemSelecionado(row);
        openModal("prossmapPreencherItem");
    };

    const columns = useMemo(
        () => [
            {
                id: "codigo",
                header: "CÓDIGO",
                accessor: (row) => row.tipo_codigo || "-",
                sortable: true,
                width: "90px",
                align: "center",
                sortValue: (row) => String(row.tipo_codigo ?? ""),
            },
            {
                id: "tipo",
                header: "TIPO",
                accessor: "tipo_nome",
                sortable: true,
                width: "160px",
            },
            {
                id: "descricao",
                header: "DESCRIÇÃO",
                accessor: "descricao",
                sortable: true,
            },
            {
                id: "resposta",
                header: "RESPOSTA",
                accessor: (row) => row.resposta || "-",
                sortable: true,
                width: "140px",
                align: "center",
            },
            {
                id: "pontos_ganhos",
                header: "PONTOS",
                accessor: (row) => (row.resposta ? formatPontos(row.pontos_ganhos) : "-"),
                sortable: true,
                width: "90px",
                align: "center",
                sortValue: (row) => Number(row.pontos_ganhos ?? 0),
            },
        ],
        []
    );

    const emptyText = prossmap
        ? "Nenhum item encontrado para este PROSSMAP."
        : "PROSSMAP não encontrado.";

    return (
        <>
            <PageContent>
                <GenericHeader responsive>
                    <Filters columns={2}>
                        <MultiselectCheck
                            options={opcoesTipoMs}
                            selectedOptions={selectedTipos}
                            setSelectedOptions={setSelectedTipos}
                            placeholder="TIPO..."
                        />
                        <FilterContainer
                            type="text"
                            placeholder="DESCRIÇÃO"
                            valor={descricao}
                            changeFunc={setDescricao}
                        />
                        <MultiselectCheck
                            options={opcoesRespostaMs}
                            selectedOptions={selectedRespostas}
                            setSelectedOptions={setSelectedRespostas}
                            placeholder="RESPOSTA..."
                        />
                        <MultiselectCheck
                            options={opcoesPreenchidoMs}
                            selectedOptions={selectedPreenchido}
                            setSelectedOptions={setSelectedPreenchido}
                            placeholder="PREENCHIDO..."
                        />
                    </Filters>

                    <ResumosRow>
                        <PontosResumo>
                            <PontosValor>
                                {progressoItens.preenchidos}/{progressoItens.total}
                            </PontosValor>
                            <PontosLabel>ITENS</PontosLabel>
                        </PontosResumo>
                        <PontosResumo>
                            <PontosValor>{formatPontos(pontuacaoProssmap)}</PontosValor>
                            <PontosLabel>PONTOS</PontosLabel>
                        </PontosResumo>
                    </ResumosRow>
                </GenericHeader>

                <TableFlexFill>
                    <SortableTable
                        columns={columns}
                        data={itensFiltrados}
                        isLoading={loading}
                        loadingText="Carregando itens..."
                        emptyText={emptyText}
                        initialSort={{ key: "codigo", direction: "asc" }}
                        getRowId={(row) => row.id}
                        onRowClick={handleAbrirItem}
                        rowCursor="pointer"
                    />
                </TableFlexFill>
            </PageContent>

            {isModalOpen("prossmapPreencherItem") && (
                <ModalPreencherItem
                    item={itemSelecionado}
                    onSaved={fetchProssmap}
                    key={itemSelecionado?.id ?? "item"}
                />
            )}
        </>
    );
};

export default Content;
